// odometer
import { onDestroy, onMount } from "@/modules/_";
import { Resize } from "@lib/subs";
import gsap, { ScrollTrigger, reduced } from "@lib/gsap";
import hey from "@lib/hey";

// payload is the odometer's `odometer-trigger-key` string
const FIRE_EVENT = "odometer:fire";

const DEFAULTS = {
  duration: 1,
  ease: "power3.out",
  elementStagger: 0.1,
  digitStagger: 0.04,
  revealDuration: 0.5,
  revealEase: "power2.out",
  triggerStart: "top 80%",
  staggerOrder: "left" as StaggerOrder,
  digitCycles: 2, // 2 copies of 0-9 ensures every digit always rolls forward
};

type StaggerOrder = "left" | "right" | "random";

type Segment = {
  type: "digit" | "static";
  char: string;
  startDigit?: number;
  hidden?: boolean;
};

type ElementData = {
  el: HTMLElement;
  rollers: { roller: HTMLElement; targetPos: number }[];
  duration: number;
  step: number;
  revealData: { el: HTMLElement; widthEm: number }[];
  originalText: string;
};

export default function (group: HTMLElement) {
  const elements = Array.from(
    group.querySelectorAll<HTMLElement>('[odometer="element"]'),
  );
  if (elements.length === 0) return;

  let timeline: gsap.core.Timeline | undefined;
  let scrollTrigger: ScrollTrigger | undefined;
  let stopResize: (() => void) | undefined;
  let externalFireHandler: ((firedKey: any) => void) | undefined;
  const elementData: ElementData[] = [];

  onMount(() => {
    if (reduced) return;

    const staggerOrder =
      (group.getAttribute("odometer-stagger-order") as StaggerOrder | null) ||
      DEFAULTS.staggerOrder;
    const triggerStart =
      group.getAttribute("odometer-trigger-start") || DEFAULTS.triggerStart;
    const elementStagger =
      parseFloat(group.getAttribute("odometer-stagger") || "") ||
      DEFAULTS.elementStagger;

    elements.forEach((el) => {
      const originalText = (el.textContent ?? "").trim();
      const hasExplicitStart = el.hasAttribute("odometer-start");
      const startValue =
        parseFloat(el.getAttribute("odometer-start") || "") || 0;
      const duration =
        parseFloat(el.getAttribute("odometer-duration") || "") ||
        DEFAULTS.duration;
      const step = getLineHeightRatio(el);

      let segments = parseSegments(originalText);
      segments = mapStartDigits(segments, startValue);
      segments = markHiddenSegments(segments, startValue);

      const grow = shouldGrow(el, hasExplicitStart, startValue, segments);
      const { rollers, revealEls } = buildRollerDOM(el, segments, step, grow);

      const fontSize = parseFloat(getComputedStyle(el).fontSize);
      const revealData = revealEls.map((revealEl) => {
        const widthEm = revealEl.offsetWidth / fontSize;
        gsap.set(revealEl, { width: 0, overflow: "hidden" });
        return { el: revealEl, widthEm };
      });

      elementData.push({
        el,
        rollers,
        duration,
        step,
        revealData,
        originalText,
      });
    });

    const ordered = applyStaggerOrder(elementData, staggerOrder);

    // ScrollTrigger.create + animation: timeline avoids a "Missing plugin?" warning
    // that fires when timeline({ scrollTrigger }) routes through gsap's plugin
    // registry and encounters a duplicate gsap copy (e.g. Webflow's global gsap).
    timeline = gsap.timeline({
      paused: true,
      onComplete() {
        elementData.forEach(({ el, originalText }) => {
          cleanupElement(el, originalText);
        });
      },
    });

    const triggerMode = group.getAttribute("odometer-trigger") || "scroll";
    const triggerKey = group.getAttribute("odometer-trigger-key");

    if (triggerMode === "external" && triggerKey) {
      externalFireHandler = (firedKey) => {
        if (firedKey === triggerKey && timeline) {
          timeline.play();
          if (externalFireHandler) {
            hey.off(FIRE_EVENT, externalFireHandler);
            externalFireHandler = undefined;
          }
        }
      };
      hey.on(FIRE_EVENT, externalFireHandler);
    } else {
      scrollTrigger = ScrollTrigger.create({
        trigger: group,
        start: triggerStart,
        once: true,
        animation: timeline,
      });
    }

    ordered.forEach((data, orderIdx) => {
      const { rollers, duration, step, revealData } = data;
      const offset = orderIdx * elementStagger;

      revealData.forEach(({ el: revealEl, widthEm }) => {
        timeline!.to(
          revealEl,
          {
            width: `${widthEm}em`,
            opacity: 1,
            duration: DEFAULTS.revealDuration,
            ease: DEFAULTS.revealEase,
          },
          offset,
        );
      });

      rollers.forEach(({ roller, targetPos }, digitIdx) => {
        // Reverse stagger so rightmost digit rolls first — matches mechanical counter direction.
        const reversedIdx = rollers.length - 1 - digitIdx;
        timeline!.to(
          roller,
          {
            y: `${-targetPos * step}em`,
            duration,
            ease: DEFAULTS.ease,
            force3D: true,
          },
          offset + reversedIdx * DEFAULTS.digitStagger,
        );
      });
    });

    stopResize = Resize.add(() => {
      elements.forEach((el) => {
        const hasRollers = el.querySelector('[data-odometer-part="roller"]');
        if (!hasRollers) return;
        const step = getLineHeightRatio(el);
        el.querySelectorAll<HTMLElement>(
          '[data-odometer-part="mask"]',
        ).forEach((mask) => {
          mask.style.height = `${step}em`;
          mask.style.lineHeight = String(step);
        });
        el.querySelectorAll<HTMLElement>(
          '[data-odometer-part="roller"]',
        ).forEach((roller) => {
          roller.style.lineHeight = String(step);
        });
        el.querySelectorAll<HTMLElement>(
          '[data-odometer-part="static"]',
        ).forEach((stat) => {
          stat.style.lineHeight = String(step);
        });
      });
      ScrollTrigger.refresh();
    });
  });

  onDestroy(() => {
    scrollTrigger?.kill();
    scrollTrigger = undefined;
    if (externalFireHandler) {
      hey.off(FIRE_EVENT, externalFireHandler);
      externalFireHandler = undefined;
    }
    timeline?.kill();
    timeline = undefined;
    stopResize?.();
    elementData.forEach(({ el, originalText }) => {
      el.innerHTML = "";
      el.textContent = originalText;
      el.style.display = "";
      el.style.alignItems = "";
      el.style.fontVariantNumeric = "";
      el.style.width = "";
      el.style.overflow = "";
      el.style.height = "";
    });
    elementData.length = 0;
  });
}

function getLineHeightRatio(el: HTMLElement): number {
  const cs = getComputedStyle(el);
  const lh = cs.lineHeight;
  if (lh === "normal") return 1.2;
  return parseFloat(lh) / parseFloat(cs.fontSize);
}

function parseSegments(text: string): Segment[] {
  return [...text].map((char) => ({
    type: /\d/.test(char) ? "digit" : "static",
    char,
  }));
}

function mapStartDigits(segments: Segment[], startValue: number): Segment[] {
  const digitSlots = segments.filter((s) => s.type === "digit");
  const padded = String(Math.floor(Math.abs(startValue)))
    .padStart(digitSlots.length, "0")
    .slice(-digitSlots.length);
  let di = 0;
  return segments.map((s) =>
    s.type === "digit"
      ? { ...s, startDigit: parseInt(padded[di++], 10) }
      : s,
  );
}

function markHiddenSegments(
  segments: Segment[],
  startValue: number,
): Segment[] {
  const totalDigits = segments.filter((s) => s.type === "digit").length;
  const absStart = Math.floor(Math.abs(startValue));
  const startDigitCount = absStart === 0 ? 1 : String(absStart).length;
  const leadingZeros = Math.max(0, totalDigits - startDigitCount);
  if (leadingZeros === 0) return segments;
  let digitsSeen = 0;
  let firstDigitSeen = false;
  let prevDigitHidden = false;
  return segments.map((seg) => {
    if (seg.type === "digit") {
      firstDigitSeen = true;
      const hidden = digitsSeen < leadingZeros;
      prevDigitHidden = hidden;
      digitsSeen++;
      return { ...seg, hidden };
    }
    const hidden = firstDigitSeen && prevDigitHidden;
    return { ...seg, hidden };
  });
}

function shouldGrow(
  el: HTMLElement,
  hasExplicitStart: boolean,
  startValue: number,
  segments: Segment[],
): boolean {
  if (el.hasAttribute("odometer-grow")) {
    return el.getAttribute("odometer-grow") !== "false";
  }
  if (!hasExplicitStart) return false;
  const absStart = Math.floor(Math.abs(startValue));
  const startDigitCount = absStart === 0 ? 1 : String(absStart).length;
  const endDigitCount = segments.filter((s) => s.type === "digit").length;
  return startDigitCount < endDigitCount;
}

function buildRollerDOM(
  el: HTMLElement,
  segments: Segment[],
  step: number,
  grow: boolean,
): {
  rollers: { roller: HTMLElement; targetPos: number }[];
  revealEls: HTMLElement[];
} {
  el.innerHTML = "";
  el.style.height = "";
  el.style.display = el.style.display || "inline-flex";
  el.style.alignItems = "center";
  el.style.fontVariantNumeric = "tabular-nums";

  const rollers: { roller: HTMLElement; targetPos: number }[] = [];
  const revealEls: HTMLElement[] = [];
  const totalCells = 10 * DEFAULTS.digitCycles;

  segments.forEach((seg) => {
    if (seg.type === "static") {
      const span = document.createElement("span");
      span.setAttribute("data-odometer-part", "static");
      Object.assign(span.style, {
        display: "inline-block",
        overflow: "clip",
        padding: "0.05em",
        margin: "-0.05em",
        height: `${step}em`,
        lineHeight: String(step),
      });
      span.textContent = seg.char;
      el.appendChild(span);
      if (grow && seg.hidden) {
        gsap.set(span, { opacity: 0 });
        revealEls.push(span);
      }
      return;
    }

    const mask = document.createElement("span");
    mask.setAttribute("data-odometer-part", "mask");
    Object.assign(mask.style, {
      display: "inline-block",
      overflow: "clip",
      padding: "0.05em",
      margin: "-0.05em",
      height: `${step}em`,
      lineHeight: String(step),
    });

    const roller = document.createElement("span");
    roller.setAttribute("data-odometer-part", "roller");
    Object.assign(roller.style, {
      display: "block",
      whiteSpace: "pre",
      textAlign: "center",
      willChange: "transform",
      lineHeight: String(step),
    });

    const digits: number[] = [];
    for (let d = 0; d < totalCells; d++) digits.push(d % 10);
    roller.textContent = digits.join("\n");
    mask.appendChild(roller);
    el.appendChild(mask);

    const startDigit = seg.startDigit ?? 0;
    const isReveal = grow && seg.hidden;
    gsap.set(roller, {
      y: isReveal ? `${step}em` : `${-startDigit * step}em`,
    });

    const endDigit = parseInt(seg.char, 10);
    // end >= start lands in first cycle; otherwise use second cycle to avoid rolling backward
    const targetPos = endDigit > startDigit ? endDigit : 10 + endDigit;
    rollers.push({ roller, targetPos });
    if (isReveal) revealEls.push(mask);
  });

  return { rollers, revealEls };
}

function cleanupElement(el: HTMLElement, originalText: string) {
  el.style.overflow = "";
  el.style.height = "";

  const digits = [...originalText].filter((c) => /\d/.test(c));
  let di = 0;

  el.querySelectorAll<HTMLElement>('[data-odometer-part="mask"]').forEach(
    (mask) => {
      const roller = mask.querySelector('[data-odometer-part="roller"]');
      if (roller) roller.remove();
      mask.textContent = digits[di++] || "";
      mask.style.opacity = "";
      mask.style.overflow = "";
    },
  );

  el.querySelectorAll<HTMLElement>('[data-odometer-part="static"]').forEach(
    (stat) => {
      stat.style.opacity = "";
    },
  );
}

function applyStaggerOrder<T>(items: T[], order: StaggerOrder): T[] {
  const arr = [...items];
  if (order === "right") return arr.reverse();
  if (order === "random") return shuffleArray(arr);
  return arr;
}

function shuffleArray<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
